import { Link } from 'react-router-dom';

/**
 * SELF OS Volume I Ch.12 framing — economic layer of intelligence.
 * Vision/direction carefully separated from what is live on Base today.
 */
export default function EconomyPage() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
      <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-gold">
        In SELF OS
      </p>
      <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold tracking-tight text-essence-ink sm:text-5xl">
        The economic layer of intelligence
      </h1>
      <p className="mt-5 max-w-2xl text-lg leading-relaxed text-essence-mute">
        In the SELF OS book, Essence is not only a token — it is the financial infrastructure through which users,
        AI agents, devices, developers, and businesses exchange value. Resonatia is where AI companies get
        inference (API keys and hosting). That paid demand funds the pool Essence uses for node rewards and agent
        settlement.
      </p>

      <p className="mt-6 max-w-2xl border-l-2 border-essence-teal/50 pl-5 text-sm leading-relaxed text-essence-mute">
        Live on Base today: token transfers, treasury/bridge contracts, cloud rewards ledger, and Talk-credit
        conversion (beta). Marketplace checkout, streaming micropayments, and machine commerce are intended
        direction — not claimed as shipping consumer products yet.
      </p>

      <section className="mt-16">
        <h2 className="font-display text-2xl font-semibold text-essence-ink">Beyond payments</h2>
        <p className="mt-3 max-w-2xl text-base text-essence-mute">
          Payments are one capability. The SELF OS design also coordinates:
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            'Subscriptions & licensing',
            'Marketplace transactions',
            'Revenue sharing & escrow',
            'Streaming / micropayments',
            'Rewards & settlement',
            'Machine commerce',
            'Economic identity & reputation',
            'Per-task / per-inference pricing',
            'Cross-border agent settlement',
          ].map((item) => (
            <li key={item} className="border-t border-essence-line pt-4 text-sm font-medium text-essence-ink">
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16 grid gap-12 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl font-semibold text-essence-ink">Human economy</h2>
          <p className="mt-3 text-base leading-relaxed text-essence-mute">
            Users interact with Essence as they would any stablecoin: buy compute, subscribe to services, pay
            for automation, receive creator or node rewards, hold or exit via the treasury rail when funded.
          </p>
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-essence-ink">Agent economy</h2>
          <p className="mt-3 text-base leading-relaxed text-essence-mute">
            Agents are economic participants — purchasing compute, data, travel, ads, or parts within limits set by
            the human. SELF OS intends policy-based delegation so users define budgets and agents execute inside
            them. That is the architecture; it is not a claim that every agent wallet is live today.
          </p>
        </div>
      </section>

      <section className="mt-16 border-t border-essence-line pt-12">
        <h2 className="font-display text-2xl font-semibold text-essence-ink">Compute economy</h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-essence-mute">
          Resonatia turns idle and edge devices into verified compute participants. AI companies buy that capacity
          for inference. Essence is the compensation and settlement rail — so spend on API keys or hosting and
          rewards for providers sit in one economic loop.
        </p>
      </section>

      <section className="mt-16 border-t border-essence-line pt-12">
        <h2 className="font-display text-2xl font-semibold text-essence-ink">Marketplace & developers</h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-essence-mute">
          The book describes a SELF Marketplace where developers publish agents, skills, models, knowledge bases,
          and automations — monetized via subscription, usage, per-task, or revenue share with Essence as native
          settlement. Treat this as platform direction. Do not read this page as “shop with Essence today.”
        </p>
      </section>

      <section className="mt-16 border-t border-essence-line pt-12">
        <h2 className="font-display text-2xl font-semibold text-essence-ink">Ownership</h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-essence-mute">
          SELF OS frames ownership of intelligence, identity, compute contributions, marketplace assets, and
          earnings as user-owned — Essence is meant to align the OS around ownership rather than lock-in.
        </p>
      </section>

      <div className="mt-14 flex flex-wrap gap-3">
        <Link
          to="/how-it-works"
          className="inline-flex rounded-lg bg-essence-teal px-5 py-3 text-sm font-semibold text-white hover:bg-essence-teal-bright"
        >
          How the loop works today
        </Link>
        <Link
          to="/for-agents"
          className="inline-flex rounded-lg border border-essence-line px-5 py-3 text-sm font-semibold text-essence-ink hover:border-essence-gold/40"
        >
          For agents
        </Link>
        <Link
          to="/contracts"
          className="inline-flex rounded-lg border border-essence-line px-5 py-3 text-sm font-semibold text-essence-ink hover:border-essence-gold/40"
        >
          Contracts
        </Link>
      </div>
    </div>
  );
}
